import { Injectable, inject } from '@angular/core';
import { signal, computed } from '@angular/core';
import { IToolCall } from '../models/tool-call.model';
import { MessageItem } from '../models/model';
import { ThreadMessagesService } from './thread-messages.service';
import { SidePanelService } from './side-panel.service';
import { TOOLS } from '../constants/tool.constants';
import { getToolStatus } from '../utils/utils';
import { TOOL_STATUS } from '../enums/enum';

@Injectable({ providedIn: 'root' })
export class ToolCallPanelService {
    private threadMessagesService = inject(ThreadMessagesService);
    private sidePanelService = inject(SidePanelService);

    isOpen = signal<boolean>(false);
    currentToolCallId = signal<string>('');
    toolCalls = signal<IToolCall[]>([]);

    // Computed: current tool call based on currentToolCallId
    currentToolCall = computed(() => {
        const calls = this.toolCalls();
        const currentId = this.currentToolCallId();
        return calls.find(call => call.id === currentId) || null;
    });

    // Computed: current index
    currentIndex = computed(() => {
        const calls = this.toolCalls();
        const currentId = this.currentToolCallId();
        return calls.findIndex(call => call.id === currentId);
    });

    // Computed: total calls
    totalCalls = computed(() => this.toolCalls().length);

    private findToolCallById(toolCallId: string | undefined) {
        if (!toolCallId) return null;
        const messages = this.threadMessagesService.messages();
        const aiMessage = messages.find(
            msg => msg.type === 'ai' && msg.tool_calls?.some((tc: any) => tc.id === toolCallId)
        );
        if (!aiMessage) return null;
        return aiMessage.tool_calls?.find((tc: any) => tc.id === toolCallId) || null;
    }

    private buildFallbackToolCall(chainItem: MessageItem, toolCallId: string): any {
        return {
            id: toolCallId,
            name: chainItem.name || chainItem.additional_kwargs?.metadata?.toolName || 'Tool',
            args: chainItem.additional_kwargs?.args || chainItem.additional_kwargs?.function?.arguments || {}
        };
    }

    /**
     * Process chainItem and open the tool call panel
     * All logic for extracting and formatting tool call data happens here
     */
    openWithChainItem(chainItem: MessageItem, toolStatus: TOOL_STATUS): void {
        if (!chainItem) return;

        // Get tool call ID from either AI message or tool result
        let toolCallId: string | undefined;

        if (chainItem.type === 'ai' && chainItem.tool_calls && chainItem.tool_calls.length > 0) {
            toolCallId = chainItem.tool_calls[0].id;
        } else if (chainItem.type === 'tool') {
            toolCallId = chainItem.tool_call_id;
        }

        if (!toolCallId) {
            console.warn('No tool call ID found in chain item:', chainItem);
            return;
        }

        // Check if this tool call already exists in our collection
        const existingToolCall = this.toolCalls().find(call => call.id === toolCallId);

        if (existingToolCall) {
            // Tool call already exists, just navigate to it
            this.openWithArtifact(toolCallId);
            return;
        }

        // Tool call doesn't exist (shouldn't happen if initialized properly), so add it
        let toolCall = chainItem.tool_calls?.[0] || this.findToolCallById(toolCallId);
        if (!toolCall) {
            toolCall = this.buildFallbackToolCall(chainItem, toolCallId);
        }

        const toolName = toolCall.name || chainItem.name || chainItem.additional_kwargs?.metadata?.toolName || 'Tool';

        // Get tool content from chain item
        let toolContent = '';
        let assistantContent = '';

        // For tool results (type === 'tool'), get content from chainItem.content
        if (chainItem.type === 'tool') {
            toolContent = chainItem.content || '';
            // Format tool content for specific tools if needed
            toolContent = this.formatToolContent(toolName, toolContent, chainItem);
            assistantContent = this.formatAssistantContent(toolName, toolCall);
        }
        // For AI tool call initiation (type === 'ai'), get content from chainItem
        else if (chainItem.type === 'ai') {
            toolContent = chainItem.content || '';
            assistantContent = this.formatAssistantContent(toolName, toolCall);

            // Try to find the matching tool result for more complete data
            const allMessages = this.threadMessagesService.messages();
            const matchingToolResult = allMessages.find(
                msg => msg.type === 'tool' && msg.tool_call_id === toolCall.id
            );

            if (matchingToolResult) {
                toolContent = matchingToolResult.content || toolContent;
                toolContent = this.formatToolContent(toolName, toolContent, matchingToolResult);
            }
        }

        // Create complete IToolCall object
        const completeToolCall: IToolCall = {
            ...toolCall,
            id: toolCall.id || toolCallId,
            name: toolName,
            status: toolStatus,
            toolContent: toolContent,
            assistantContent: assistantContent,
            response_metadata: toolCall.response_metadata || null
        } as IToolCall;


        // Add to service
        this.addToolCall(completeToolCall);

        // Open panel with this tool call
        this.openWithArtifact(completeToolCall.id);
    }

    openWithArtifact(toolCallId: string): void {
        this.currentToolCallId.set(toolCallId);
        this.isOpen.set(true);
        this.sidePanelService.open('TOOL_DETAILS', { toolCallId });
    }

    addToolCall(toolCall: IToolCall): void {
        // Check if tool call already exists to avoid duplicates
        const existingIndex = this.toolCalls().findIndex(call => call.id === toolCall.id);
        if (existingIndex >= 0) {
            // Update existing tool call
            const updated = [...this.toolCalls()];
            updated[existingIndex] = toolCall;
            this.toolCalls.set(updated);
        } else {
            // Add new tool call
            this.toolCalls.set([...this.toolCalls(), toolCall]);
        }
        // Set as current if not already set
        if (!this.currentToolCallId()) {
            this.currentToolCallId.set(toolCall.id);
        }
    }

    private formatAssistantContent(toolName: string, toolCallData: any): string {
        const normalizedName = toolName.toLowerCase().replace(/[-_]/g, '') as TOOLS;

        switch (normalizedName) {
            case TOOLS.LIST_FILES:
                return 'List all files';

            case TOOLS.READ_FILE:
                const filePath = toolCallData?.args?.file_path || toolCallData?.function?.arguments?.file_path;
                if (filePath) {
                    try {
                        const args = typeof filePath === 'string' ? JSON.parse(filePath) : filePath;
                        return `Read file: ${args}`;
                    } catch {
                        return `Read file: ${filePath}`;
                    }
                }
                return 'Read file';

            case TOOLS.WRITE_FILE:
            case TOOLS.EDIT_FILE:
            case TOOLS.STR_REPLACE:
            case TOOLS.SEARCH_REPLACE:
                const editFilePath = toolCallData?.args?.file_path || toolCallData?.function?.arguments?.file_path;
                if (editFilePath) {
                    try {
                        const args = typeof editFilePath === 'string' ? JSON.parse(editFilePath) : editFilePath;
                        return `Edit file: ${args}`;
                    } catch {
                        return `Edit file: ${editFilePath}`;
                    }
                }
                return 'Edit file';

            case TOOLS.EXECUTE_COMMAND:
                const command = toolCallData?.args?.command || toolCallData?.function?.arguments?.command;
                if (command) {
                    return `Execute: ${command}`;
                }
                return 'Execute command';

            default:
                return toolName
                    .split(/[-_]/)
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
        }
    }

    private formatToolContent(toolName: string, toolContent: string, chainItem: MessageItem): string {
        const normalizedName = toolName.toLowerCase().replace(/[-_]/g, '') as TOOLS;

        // For WRITE_FILE and EDIT_FILE, format the content specially
        if (normalizedName === TOOLS.WRITE_FILE || normalizedName === TOOLS.EDIT_FILE) {
            try {
                const parsedContent = JSON.parse(toolContent);

                // Try to get file name from tool_calls
                let fileName = '';
                if (chainItem.tool_calls?.[0]?.args?.file_path) {
                    fileName = chainItem.tool_calls[0].args.file_path;
                }

                // Format as expected by the tool view
                if (parsedContent._metadata?.old_content && fileName) {
                    return JSON.stringify({
                        file_name: fileName,
                        old_content: parsedContent._metadata.old_content,
                        new_content: parsedContent.new_content || parsedContent.content || toolContent
                    });
                }
            } catch (e) {
                // If parsing fails, return original content
                console.warn('Failed to parse tool content:', e);
            }
        }

        return toolContent;
    }

    navigateToToolCall(toolCallId: string): void {
        const exists = this.toolCalls().some(call => call.id === toolCallId);
        if (exists) {
            this.currentToolCallId.set(toolCallId);
        }
    }

    navigateToPrevious(): void {
        const index = this.currentIndex();
        if (index > 0) {
            const previousCall = this.toolCalls()[index - 1];
            this.currentToolCallId.set(previousCall.id);
        }
    }

    navigateToNext(): void {
        const index = this.currentIndex();
        const total = this.totalCalls();
        if (index < total - 1) {
            const nextCall = this.toolCalls()[index + 1];
            this.currentToolCallId.set(nextCall.id);
        }
    }

    clearToolCalls(): void {
        this.toolCalls.set([]);
        this.currentToolCallId.set('');
    }

    /**
     * Initialize all tool calls from messages
     * This should be called when messages are loaded to populate the navigator
     */
    initializeToolCallsFromMessages(messages: MessageItem[]): void {
        const toolCalls: IToolCall[] = [];

        // Find all AI messages with tool_calls
        const aiMessagesWithToolCalls = messages.filter(
            msg => msg.type === 'ai' && msg.tool_calls && msg.tool_calls.length > 0
        );

        // Process each AI message with tool calls
        for (const aiMessage of aiMessagesWithToolCalls) {
            const toolCallsInMessage = aiMessage.tool_calls || [];

            for (const toolCall of toolCallsInMessage) {
                // Find matching tool result
                if (![TOOLS.WRITE_TODOS, TOOLS.PRESENT_FILES].includes(toolCall.name as TOOLS)) {
                    const toolResult = messages.find(
                        msg => msg.type === 'tool' && msg.tool_call_id === toolCall.id
                    );

                    // Determine status
                    const status = getToolStatus(toolResult);
                    let toolContent = '';
                    if (toolResult) {
                        toolContent = toolResult.content || '';
                        toolContent = this.formatToolContent(toolCall.name, toolContent, toolResult);
                    }

                    // Format assistant content
                    const assistantContent = this.formatAssistantContent(toolCall.name, toolCall);
                    const responseMetadata = (toolResult as any)?.['response_metadata'];

                    // Create IToolCall object
                    const completeToolCall: IToolCall = {
                        ...toolCall,
                        status: status,
                        toolContent: toolContent,
                        assistantContent: assistantContent,
                        ...(responseMetadata != null && { response_metadata: responseMetadata })
                    } as IToolCall;

                    toolCalls.push(completeToolCall);
                }
            }
        }

        // Set all tool calls at once
        this.toolCalls.set(toolCalls);
    }
}
